<template>
  <div class="language-mix-row">
    <div class="mix-row-label">{{ $t('account.languageMix.label') }}</div>
    <div class="mix-row-value">
      <div
        class="mix-options"
        role="radiogroup"
        :aria-label="$t('account.languageMix.label')"
      >
        <button
          v-for="option in options"
          :key="option.id ?? 'default'"
          type="button"
          class="mix-option"
          :class="{ active: selectedOverride === option.id }"
          role="radio"
          :aria-checked="selectedOverride === option.id"
          @click="selectOverride(option.id)"
        >
          <span class="mix-option-name">{{ $t(option.labelKey) }}</span>
          <span class="mix-option-descriptor">{{ $t(option.descriptorKey) }}</span>
        </button>
      </div>
      <p class="mix-note">{{ $t('account.languageMix.forwardNote') }}</p>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { getDatabase, ref as dbRef, get, set, remove } from 'firebase/database';
import { useI18n } from 'vue-i18n';
import { authStore } from '@/store/auth';
import { useToast } from '@/composables/useToast.js';

// The stored ids are the canonical strings the RTDB .validate pins and the
// backend LanguageMixOverride enum maps; `null` is the default and is stored as
// the ABSENCE of the key, not as a fourth value. Keeping "no override" out of
// the data is what lets the tutor's own assessment drive the mix for everyone
// who never opens this control.
const OPTIONS = [
  {
    id: null,
    labelKey: 'account.languageMix.defaultOption',
    descriptorKey: 'account.languageMix.defaultDescriptor',
  },
  {
    id: 'immersion',
    labelKey: 'account.languageMix.immersion',
    descriptorKey: 'account.languageMix.immersionDescriptor',
  },
  {
    id: 'balanced',
    labelKey: 'account.languageMix.balanced',
    descriptorKey: 'account.languageMix.balancedDescriptor',
  },
  {
    id: 'nativeHeavy',
    labelKey: 'account.languageMix.nativeHeavy',
    descriptorKey: 'account.languageMix.nativeHeavyDescriptor',
  },
];
const STORED_IDS = OPTIONS.map((option) => option.id).filter(Boolean);

export default {
  name: 'LanguageMixPicker',
  props: {
    langCode: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n();
    const { showToast } = useToast();
    // No access gate: the adaptiveLanguageMix flag that once decided whether to
    // render this row was retired once the level-adaptive policy was on for
    // everyone, so the control is offered to every signed-in account with a
    // selected language.
    const selectedOverride = ref(null);
    // Guards the async initial read: bumped by every new load AND by a user
    // selection, so a slow get() can never overwrite a choice the user just
    // made (or a newer language's load). Same reasoning as TutorVoicePicker.
    let loadToken = 0;

    const overridePath = () =>
      `users/${authStore.getUserId()}/languagePreferences/${props.langCode}/languageMixOverride`;

    const loadSelection = async () => {
      const token = ++loadToken;
      // Clear first, because what is on screen belongs to whichever language
      // was loaded last. Leaving it up during the fetch means a learner who
      // switches language and immediately taps the option that happens to match
      // the PREVIOUS language's value hits the "already selected" early return
      // in selectOverride, and their choice is silently dropped for the new
      // language. Showing the default for the moment the value is unknown is
      // the honest state.
      selectedOverride.value = null;
      try {
        const snap = await get(dbRef(getDatabase(), overridePath()));
        if (token !== loadToken) return; // stale: user picked or language changed
        const stored = snap.val();
        // An unrecognised stored value reads as "default" here for the same
        // reason LanguageMixOverride.fromStoredValue returns None for it on the
        // backend: the two must agree about what the learner is being told.
        selectedOverride.value = STORED_IDS.includes(stored) ? stored : null;
      } catch (error) {
        if (token !== loadToken) return;
        console.error('Error loading language mix override:', error);
        selectedOverride.value = null;
      }
    };
    watch(() => props.langCode, loadSelection, { immediate: true });

    const selectOverride = async (overrideId) => {
      if (overrideId === selectedOverride.value) return;
      // Token does double duty: invalidates any in-flight initial read AND
      // marks this write, so a failure from a superseded pick is dropped
      // instead of clobbering a newer selection with a stale revert.
      const token = ++loadToken;
      selectedOverride.value = overrideId;
      try {
        // No write queue: the RTDB client sends every write for a path over one
        // connection in the order it was issued, so two rapid taps cannot land
        // out of order and leave the older choice stored under the newer one on
        // screen. The token below covers the other half, a failure arriving
        // after a newer tap has taken over the UI.
        const node = dbRef(getDatabase(), overridePath());
        // Choosing the default DELETES the key rather than writing a fourth
        // value, so absence stays the single meaning of "no override" for the
        // backend read and the admin panel alike.
        await (overrideId === null ? remove(node) : set(node, overrideId));
      } catch (error) {
        if (token !== loadToken) return; // a newer pick owns the UI now
        console.error('Error saving language mix override:', error);
        showToast(t('account.languageMix.saveError'), 'error');
        // Resync from the server rather than guessing at the pre-click value:
        // after rapid toggles the DB truth may not be `previous`.
        await loadSelection();
      }
    };

    return {
      options: OPTIONS,
      selectedOverride,
      selectOverride,
    };
  },
};
</script>

<style scoped>
/* Mirrors AccountView's .info-row / .info-label look and TutorVoicePicker's
   row, which sits directly above this one (those styles are scoped to their own
   components and cannot reach in here). Keep the desktop breakpoint in sync
   with AccountView's .info-row media query. */
.language-mix-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--al-border);
  min-height: 44px;
}

.mix-row-label {
  font-weight: 600;
  color: var(--al-text-muted);
  margin-bottom: 0.5rem;
}

@media (min-width: 768px) {
  .language-mix-row {
    flex-direction: row;
  }

  .mix-row-label {
    flex: 0 0 40%;
    margin-bottom: 0;
  }
}

/* Four options with a sentence of explanation each: a vertical stack stays
   readable on a phone, where two-across cards would force the descriptors into
   three or four wrapped lines. */
.mix-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 32.75rem;
}

.mix-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* `start`, not `left`, so the label side follows the script direction. */
  text-align: start;
  gap: 0.125rem;
  padding: 0.5rem 0.75rem;
  background: transparent;
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-sm);
  cursor: pointer;
  min-height: 48px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.mix-option.active {
  border-color: var(--al-primary);
  box-shadow: 0 0 0 1px var(--al-primary);
}

.mix-option-name {
  font-weight: 600;
  color: var(--al-text);
}

.mix-option.active .mix-option-name {
  /* --al-text-link, not --al-primary-hover: the two are the same #1B75D0 in
     light mode, but --al-primary-hover is a fixed hue token and #1B75D0 is only
     about 2.7:1 on the dark raised surface, which fails AA. --al-text-link is
     the theme-aware token for exactly this case and swaps to #2D90EB in dark.
     (TutorVoicePicker's equivalent rule still uses the hue token and has the
     same dark-mode contrast gap; fixing that is its own change.) */
  color: var(--al-text-link);
}

.mix-option-descriptor {
  font-size: 0.8125rem;
  color: var(--al-text-muted);
}

.mix-note {
  margin: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: var(--al-text-muted);
}
</style>
